import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { SwiperOnboarding } from "../swiper-onboarding/swiper-onboarding";
import { OnboardingArrowRightIcon } from "../../../assets/icon";
import { DotsIndicator } from "../dots-indicator-onboarding/dots-indicator-onboarding";
import images from "../../../assets/images";
import { usePreloadImages } from "../../../hooks/usePreloadImages";

interface GenerateOnbordingProps {
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handleSkip: () => void;
}

interface SlideText {
  title: string;
  descrition: string;
}

export const GenerateOnbording: React.FC<GenerateOnbordingProps> = ({
  handlePrevPage,
  handleNextPage,
  handleSkip,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isCompactLandscape = useMediaQuery(
    "(orientation: landscape) and (max-width: 1366px)",
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const footerImage = isMobile
    ? images.OnboardingChatFooterMB
    : images.OnboardingChatFooterDS;
  const headerImage = isMobile
    ? images.OnboardingChatHeaderMB
    : images.OnboardingChatHeaderDS;
  const testImage = isMobile
    ? images.OnboardingChatTestMB
    : images.OnboardingChatTestDS;

  const imagesToPreload = [footerImage, headerImage, testImage];
  const imagesLoaded = usePreloadImages(imagesToPreload);

  const slideTexts: SlideText[] = [
    {
      title: "Generate Lessons",
      descrition: "Select “Retrieve a lesson” in the menu to start learning.",
    },
    {
      title: "Study effectively",
      descrition:
        "Our interactive lessons and engaging tests are built to challenge and elevate your skills. Pick a course, explore new ideas, and grow at your own pace.",
    },
    {
      title: "Master your Knowledge",
      descrition:
        "Our interactive tests adapt to your pace and expertise, providing immediate feedback & celebrating your milestones.",
    },
    {
      title: "Generate Lessons",
      descrition: "Select “Retrieve a lesson” in the menu to start learning.",
    },
    {
      title: "Study effectively",
      descrition:
        "Our interactive lessons and engaging tests are built to challenge and elevate your skills. Pick a course, explore new ideas, and grow at your own pace.",
    },
    {
      title: "Master your Knowledge",
      descrition:
        "Our interactive tests adapt to your pace and expertise, providing immediate feedback & celebrating your milestones.",
    },
  ];

  const [currentText, setCurrentText] = useState<SlideText>(slideTexts[0]);

  useEffect(() => {
    const checkSize = () => {
      if (contentRef.current && containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const containerWidth = containerRef.current.clientWidth;
        const contentHeight = contentRef.current.scrollHeight;
        const contentWidth = contentRef.current.scrollWidth;

        const safetyMargin = isCompactLandscape ? 0.96 : 0.98;
        const heightScale = containerHeight / contentHeight;
        const widthScale = containerWidth / contentWidth;
        const fittedScale = (
          isCompactLandscape ? heightScale : Math.min(heightScale, widthScale)
        ) * safetyMargin;
        const minScale = isCompactLandscape ? 0 : isMobile ? 0.55 : 0.8;

        setScale(Math.max(Math.min(fittedScale, 1), minScale));
      }
    };

    checkSize();
    window.addEventListener("resize", checkSize);
    window.addEventListener("orientationchange", checkSize);
    const timer = setTimeout(checkSize, 300);

    return () => {
      window.removeEventListener("resize", checkSize);
      window.removeEventListener("orientationchange", checkSize);
      clearTimeout(timer);
    };
  }, [imagesLoaded, isCompactLandscape, isMobile]);

  if (!imagesLoaded) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        overflowY: isCompactLandscape || isMobile ? "hidden" : "auto",
        overflowX: "hidden",
        display: "flex",
        flex: 1,
        flexDirection: "column",
        justifyContent: "space-around",
        height: "100dvh",
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          width: "100%",
        }}
      >
        <Box>
          <SwiperOnboarding
            onSlideChange={(swiper) => {
              const index =
                typeof swiper.realIndex !== "undefined"
                  ? swiper.realIndex
                  : swiper.activeIndex % slideTexts.length;
              setCurrentText(slideTexts[index]);
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: isCompactLandscape
              ? { xs: "20px", sm: "24px", md: "28px" }
              : { xs: "60px", sm: "52px", md: "40px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              maxWidth: { xs: "340px", sm: "340px", md: "700px" },
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: "#0E0E0E",
                fontSize: isCompactLandscape
                  ? { xs: "22px", sm: "24px", md: "28px" }
                  : { xs: "32px", sm: "32px", md: "40px" },
                fontWeight: 700,
                textAlign: "center",
                height: isCompactLandscape ? "52px" : "80px",
                lineHeight: "120%",
              }}
            >
              {currentText.title === "Master your Knowledge" ? (
                <>
                  Master your{" "}
                  <span
                    style={{
                      background: "linear-gradient(90deg, #8957CF, #28C9DC)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Knowledge
                  </span>
                </>
              ) : (
                currentText.title
              )}
            </Typography>

            <Typography
              sx={{
                fontSize: isCompactLandscape ? "13px" : "18px",
                fontWeight: 600,
                color: "#84909F",
                maxWidth: { xs: "342px", sm: "342px", md: "380px" },
                height: isCompactLandscape ? "72px" : "108px",
                textAlign: "center",
                marginTop: "8px",
              }}
            >
              {currentText.descrition}
            </Typography>

            <Button
              onClick={handleNextPage}
              sx={{
                marginTop: isCompactLandscape ? "8px" : "15px",
                fontSize: isCompactLandscape ? "14px" : "18px",
                background: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
                color: "#FFFFFF",
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: "999px",
                px: isCompactLandscape ? "22px" : "32px",
                py: isCompactLandscape ? "9px" : "16px",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #20B6C8 0%, #7C4ABF 100%)",
                },
              }}
            >
              {"Next"}
            </Button>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "100%",
                alignItems: "center",
                marginBottom: { xs: "8px", sm: "10px" },
                marginTop: { xs: "10px", sm: "12px" },
                paddingX: { xs: "8px", sm: "12px" },
              }}
            >
              <OnboardingArrowRightIcon
                style={{ width: "30px", cursor: "pointer" }}
                onClick={handlePrevPage}
              />
              <DotsIndicator activeIndex={4} />
              <Typography
                onClick={handleSkip}
                sx={{
                  fontSize: { xs: "14px", sm: "16px" },
                  color: "#B6BAD5",
                  cursor: "pointer",
                }}
              >
                Skip
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
